import {
  ConnectionModeEnum,
  ConnectionType,
  getUnifiedLiquiditySymbolsByMarket,
  HStack,
  Icon,
  IconName,
  IndicatorBadge,
  IndicatorBadgeSizes,
  ProductTypeEnum,
  SecurityTypesUtils,
  Text,
  useMarketAccountsContext,
  useSecuritiesContext,
  VStack,
  type Market,
  type MarketSecurityStatus,
  type Security,
  type UnifiedLiquidityToken,
} from '@talos/kyoko';
import { compact, uniq } from 'lodash-es';
import { useMemo } from 'react';
import { MULTILEG_MARKET_ACCOUNT } from '../../../providers/OMSContext.types';
import { getRFQSyntheticCrossSymbol } from '../../../utils/rfq';
import { AvailabilityEnum, type AvailabilityCondition, type AvailabilityResult } from '../types';

/** A hook wrapper for getMSSEnabledCondition */
export const useMSSEnabledCondition = ({
  connectionType,
  mssByAccountBySymbol,
  symbol,
  ulTokensForSymbol,
  justWarnOnFailure,
}: Omit<GetMSSEnabledConditionParams, 'securitiesBySymbol'>): AvailabilityCondition => {
  const { securitiesBySymbol } = useSecuritiesContext();

  return useMemo(
    () =>
      getMSSEnabledCondition({
        connectionType,
        mssByAccountBySymbol,
        symbol,
        ulTokensForSymbol,
        justWarnOnFailure,
        securitiesBySymbol,
      }),
    [connectionType, mssByAccountBySymbol, symbol, ulTokensForSymbol, securitiesBySymbol, justWarnOnFailure]
  );
};

export interface GetMSSEnabledConditionParams {
  connectionType: ConnectionType;
  /** The complete map of MSS records to base the condition checking on */
  mssByAccountBySymbol: Map<string, Map<string, MarketSecurityStatus>> | undefined;
  symbol: string | undefined;
  /** When using unified liquidity, pass the token list for the selected symbol here */
  ulTokensForSymbol?: UnifiedLiquidityToken[];
  /** If true, will just Warn instead of Disabling the item on condition failure */
  justWarnOnFailure?: boolean;
  /** Basic refdata */
  securitiesBySymbol: Map<string, Security>;
}

/**
 * Returns an AvailabilityCondition used to check if a MarketSecurityStatus.Enabled is Up or not.
 *
 * If we at any point are not able to resolve an MSS, the condition will return available: true.
 */
export function getMSSEnabledCondition({
  connectionType,
  mssByAccountBySymbol,
  symbol,
  ulTokensForSymbol,
  justWarnOnFailure,
  securitiesBySymbol,
}: GetMSSEnabledConditionParams): AvailabilityCondition {
  const failureAvailability = justWarnOnFailure ? AvailabilityEnum.Warning : AvailabilityEnum.Disabled;
  const getSymbolsToCheck = getSymbolsToValidateForMarketFn(
    symbol,
    ulTokensForSymbol,
    securitiesBySymbol,
    connectionType
  );

  return {
    id: `mss-enabled`,
    ready: mssByAccountBySymbol != null && symbol != null,
    condition: (market, marketAccount) => {
      if (!symbol) {
        // This is just a typing artefact. If there is no symbol, we will not be given any markets to validate.
        return { availability: AvailabilityEnum.Ok };
      }

      if (!mssByAccountBySymbol) {
        // If we fail to load the map at all, we default to showing all.
        return { availability: AvailabilityEnum.Ok };
      }

      if (!marketAccount) {
        // MarketSecurityStatuses are only applicable for market accounts
        return { availability: AvailabilityEnum.Ok };
      }

      if (marketAccount.Name === MULTILEG_MARKET_ACCOUNT) {
        // For multileg specifically, the backend doesn't do any MSS Status=Enabled validation, so we don't either. Yes, its insane.
        return { availability: AvailabilityEnum.Ok };
      }

      const security = securitiesBySymbol.get(symbol);
      if (SecurityTypesUtils.Compound.isProductType(security)) {
        // Compound securities do not generate MSS records, so no validation on them.
        return { availability: AvailabilityEnum.Ok };
      }

      const results: MSSConditionSymbolLevelResult[] = getSymbolsToCheck(market).map(symbol => {
        const mss = mssByAccountBySymbol.get(marketAccount.Name)?.get(symbol);

        if (!mss) {
          return { symbol, availability: failureAvailability, failedReason: 'No MSS found' };
        }

        return mss.Enabled === ConnectionModeEnum.Up
          ? { symbol, availability: AvailabilityEnum.Ok }
          : { symbol, availability: failureAvailability, failedReason: 'Symbol disabled' };
      });

      const accountLevelAvailability = getMSSConditionAccountLevelAvailability(results);

      if (accountLevelAvailability === AvailabilityEnum.Ok) {
        return { availability: AvailabilityEnum.Ok };
      }

      return {
        availability: accountLevelAvailability,
        render: () => (
          <MSSConditionInfo
            accountName={marketAccount.Name}
            selectedSymbol={symbol}
            symbolLevelResults={results}
            unifiedLiquidityEnabled={!!ulTokensForSymbol}
          />
        ),
        _text: getMSSConditionText(results),
      } satisfies AvailabilityResult;
    },
  };
}

export function MSSConditionInfo({
  accountName,
  selectedSymbol,
  symbolLevelResults,
  unifiedLiquidityEnabled,
}: {
  accountName: string;
  selectedSymbol: string;
  symbolLevelResults: MSSConditionSymbolLevelResult[];
  unifiedLiquidityEnabled: boolean;
}) {
  const { securitiesBySymbol } = useSecuritiesContext();
  const { marketAccountsByName } = useMarketAccountsContext();
  const marketAccount = marketAccountsByName.get(accountName);

  const someResultHasDifferentSymbol = symbolLevelResults.some(result => result.symbol !== selectedSymbol);
  const showPerSymbolFailureReason =
    unifiedLiquidityEnabled || someResultHasDifferentSymbol || symbolLevelResults.length > 1;

  if (!showPerSymbolFailureReason) {
    const result = symbolLevelResults.at(0);
    return result && 'failedReason' in result ? result.failedReason : undefined;
  }

  // Multi-symbol validation rendering. Used for cases such as UL or RFQ Synthetic Crossing
  return (
    <VStack gap="spacing4" minWidth="220px" alignItems="flex-start" p="spacing2">
      <Text fontWeight="bold" color="colorTextSubtle">
        {marketAccount?.DisplayName ?? accountName}
      </Text>
      {symbolLevelResults.map(result => {
        const security = securitiesBySymbol.get(result.symbol);
        return (
          <HStack w="100%" key={result.symbol} justifyContent="space-between" gap="spacing12">
            <HStack gap="spacing2">
              <Text color="colorTextAttention">{security?.DisplaySymbol ?? result.symbol}</Text>
              {result.availability < AvailabilityEnum.Ok && <Icon icon={IconName.DotSolid} color="yellow.lighten" />}
            </HStack>

            <IndicatorBadge size={IndicatorBadgeSizes.Small}>
              <HStack gap="spacing2" alignItems="flex-start">
                <Icon icon={result.availability === AvailabilityEnum.Ok ? IconName.Check : IconName.Close} />
                {'failedReason' in result && <Text>{result.failedReason}</Text>}
              </HStack>
            </IndicatorBadge>
          </HStack>
        );
      })}
    </VStack>
  );
}

export type MSSConditionSymbolLevelResult =
  | {
      symbol: string;
      availability: typeof AvailabilityEnum.Ok;
    }
  | {
      symbol: string;
      availability: typeof AvailabilityEnum.Disabled | typeof AvailabilityEnum.Warning;
      failedReason: string;
    };

/**
 * Generates simplified text from MSS condition results for testing purposes.
 */
export function getMSSConditionText(results: MSSConditionSymbolLevelResult[]): string {
  const failed = results.filter(
    (r): r is MSSConditionSymbolLevelResult & { failedReason: string } => 'failedReason' in r
  );
  if (failed.length === 0) {
    return '';
  }
  if (failed.length === 1) {
    return failed[0].failedReason;
  }
  return `${failed.length} symbols have issues`;
}

/**
 * For MSS Conditions we support the unified liquidity case where we have one availability for each symbol within the account
 *
 * This function takes in all the availabilities, one coming from each symbol, and pushes out what the availability at the account-level
 * should be.
 */
export function getMSSConditionAccountLevelAvailability(
  symbolLevelResults: MSSConditionSymbolLevelResult[]
): AvailabilityEnum {
  const { ok, disabled } = symbolLevelResults.reduce(
    (agg, result) => {
      return {
        ok: result.availability === AvailabilityEnum.Ok ? agg.ok + 1 : agg.ok,
        disabled: result.availability === AvailabilityEnum.Disabled ? agg.disabled + 1 : agg.disabled,
      };
    },
    { ok: 0, disabled: 0 }
  );

  const allOk = ok === symbolLevelResults.length;
  if (allOk) {
    return AvailabilityEnum.Ok;
  }

  const allDisabled = disabled === symbolLevelResults.length;
  if (allDisabled) {
    return AvailabilityEnum.Disabled;
  }

  // Else, lastly, we have some mixture of Ok, Disabled, Warn. We at least know that we're not entirely OK, and we're not entirely disabled.
  // Hence, we're in between: return Warning.
  return AvailabilityEnum.Warning;
}

/**
 * A helper function for the MSS conditions used to validate market accounts
 *
 * Given a symbol and any available UL tokens (which are to be passed when UL is enabled),
 * will return a function which can be called with a market name to get back the symbols which should be validated
 */
export const getSymbolsToValidateForMarketFn = (
  symbol: string | undefined,
  ulTokensForSymbol: UnifiedLiquidityToken[] | undefined,
  securitiesBySymbol: Map<string, Security>,
  connectionType: ConnectionType
): ((market: Market) => string[]) => {
  const ulSymbolsByMarket = ulTokensForSymbol ? getUnifiedLiquiditySymbolsByMarket(ulTokensForSymbol) : undefined;
  const security = symbol ? securitiesBySymbol.get(symbol) : undefined;
  const securityMarketsSet = new Set(security?.Markets ?? []);
  const ulSymbols = uniq(ulTokensForSymbol?.map(t => t.Symbol) ?? []);

  return (market: Market) => {
    if (!security) {
      return [];
    }

    // For Spot, we expand to _all_ UL symbols, not only the ones relevant to the Market.
    if (security.ProductType === ProductTypeEnum.Spot) {
      if (connectionType === ConnectionType.RFQ && !securityMarketsSet.has(market.Name)) {
        // If there is some market selected which is not in the security's set for Spot, then this market is selected via RFQ Synthetic Crossing.
        return compact([getRFQSyntheticCrossSymbol(security)]);
      }

      return uniq([security.Symbol, ...ulSymbols]);
    }

    // For non-spot below here, we are indexing by Market, and only returning UL symbols relevant to the Market.
    const ulSymbolsForMarket = ulSymbolsByMarket?.get(market.Name);
    const symbols: string[] = ulSymbolsForMarket ? [...ulSymbolsForMarket] : [];

    // If the base symbol the UL is "unified with" is of the same market, then include it
    const includeBaseSymbol = security?.Markets?.includes(market.Name);
    if (includeBaseSymbol) {
      symbols.unshift(security.Symbol);
    }

    return symbols;
  };
};
